import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSupabaseClient } from '../../data/supabase-client';
import { groupByBucket, useMyDay } from '../../data/queries/use-my-day';
import { useProjects } from '../../data/queries/use-projects';
import {
  completeMyDayItem,
  createMyDayItem,
  describeMyDayFailure,
  dismissMyDayItem,
  endMyDaySeries,
  isStaleMyDayWrite,
  linkMyDayCapture,
  reopenMyDayItem,
  rescheduleMyDayItem,
  setMyDayRecurrence,
  skipMyDayOccurrence,
  snoozeMyDayItem,
  undoMyDayOccurrence,
  unlinkMyDayCapture,
  unsnoozeMyDayItem,
  updateMyDayItem,
} from '../../data/writers/my-day-writers';
import { useAppUser } from '../../data/queries/use-app-user';
import { captureDetailPath } from '../sol/sol-capture-route';
import { formatInstant } from '../../lib/timezone';
import { wallTimeToInstant } from './my-day-time';
import { myDayNotificationService } from './notification-service';
import { MY_DAY_HISTORY_PATH } from './my-day-route';
import type {
  CaptureOrganizationRow,
  CaptureRow,
  MyDayBucket,
  MyDayListRow,
  MyDayPriority,
  MyDayRecurRule,
  ProjectRow,
} from '../../data/database.types';
import './my-day.css';
// **THE SHEET SKIN THE APP ALREADY HAS.** `AddFollowUpSheet` wears the New
// Project sheet's classes (design export S04) rather than inventing a second
// sheet vocabulary, so the stylesheet that defines them is imported here
// explicitly instead of being relied on as a side effect of another screen
// having been rendered first.
import '../projects/projects.css';

/**
 * MY DAY — M12 / S12-01, the first visible slice.
 *
 * **THE SMALLEST THING BRANDON CAN ACTUALLY USE AND JUDGE.** Write a follow-up,
 * see it in the right section, tick it off, undo that, and open the record it
 * came from. Everything else M12 will eventually own — notifications,
 * recurrence, snooze, priority, Sol writing these for him — is deliberately
 * absent, because the product model has to feel right before any of it is worth
 * building on top.
 *
 * **NO DATE IS COMPARED IN THIS FILE.** Overdue / Today / Upcoming arrive
 * already decided by `list_my_day_items`, computed in the canonical Crewlink
 * timezone (D-009, INV-T17). A screen that worked out "today" for itself would
 * be a second answer to the same question, and the two would disagree at
 * midnight — the one hour where being wrong is guaranteed to be noticed.
 *
 * **LEAVING THE TIME EMPTY MEANS DAY-ONLY.** Nothing here invents 8am, 9am or
 * noon. *"Friday"* and *"Friday at 9"* are different promises, and a follow-up
 * that quietly became the second one would go overdue while he still had all
 * day.
 *
 * **AND A TIME IS AUTHORED IN THE CANONICAL CREWLINK TIMEZONE, NEVER THE
 * DEVICE'S** (the S12-01 review blocker). A phone in Manila with Crewlink set
 * to New York must produce *"9am New York"*: "9am" is a promise about his
 * working day, not about where his handset is. **`readDeviceTimeZone` is
 * deliberately not imported here.**
 */
export function MyDayScreen() {
  const { status, items, error, reload } = useMyDay();
  /**
   * **THE CANONICAL ZONE COMES FROM THE PROFILE, NOT FROM A ROW.** Reading it
   * off `items[0]` meant an empty My Day had no zone at all — and the authoring
   * form needs one precisely when there is nothing in the list yet.
   */
  const { profile } = useAppUser();
  const timezone = profile?.timezone ?? null;
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * **WD-3: THE HONEST "NOTIFICATIONS ARE OFF" STATE.** True only on a native
   * device where permission is DENIED. It renders one quiet line — no prompt,
   * no dialog, no nagging — and only while a timed follow-up exists to be
   * missed. Re-checked when the list changes, so enabling notifications in
   * the phone's Settings makes the line disappear on the next reconcile.
   */
  const [notificationsDenied, setNotificationsDenied] = useState(false);
  useEffect(() => {
    let alive = true;
    void myDayNotificationService()
      .isDenied()
      .then((denied) => {
        if (alive) setNotificationsDenied(denied);
      });
    return () => {
      alive = false;
    };
  }, [items]);
  /**
   * The last item taken off the list, so Undo has something to undo — and
   * **which way it left**, because the recovery has to tell the server what it
   * believed. Two kinds now (WD-1): a one-time item leaves by STATE and comes
   * back through `reopen`; a recurring item leaves by ADVANCING, and comes back
   * through `undo_my_day_occurrence`, which needs **the `updated_at` the
   * advance itself returned** — the version token proving nothing has happened
   * since. Done, Dismissed and Skipped are different facts; one bar.
   */
  const [undoable, setUndoable] = useState<
    | { kind: 'state'; id: string; title: string; from: 'done' | 'dismissed' }
    | { kind: 'occurrence'; id: string; title: string; updatedAt: string; what: 'done' | 'skipped' }
    | null
  >(null);
  const [adding, setAdding] = useState(false);
  /** The item whose Edit / Reschedule sheet is open, if any. */
  const [editing, setEditing] = useState<MyDayListRow | null>(null);
  const [rescheduling, setRescheduling] = useState<MyDayListRow | null>(null);
  /** The row whose compact action menu is open. */
  const [menuFor, setMenuFor] = useState<MyDayListRow | null>(null);
  const [snoozing, setSnoozing] = useState<MyDayListRow | null>(null);
  /** The row a Capture is being chosen for. */
  const [linking, setLinking] = useState<MyDayListRow | null>(null);

  const buckets = groupByBucket(items);

  const onDone = (item: MyDayListRow): void => {
    void markDone(item);
  };

  async function markDone(item: MyDayListRow): Promise<void> {
    const client = getSupabaseClient();
    if (!client) return;
    setNotice(null);
    try {
      // The state the screen was showing. If it moved somewhere else, the
      // database refuses rather than re-stamping a newer one.
      const row = await completeMyDayItem(client, item.id, 'open');
      // **THE SERVER SPEAKS FIRST.** The list is re-read rather than edited in
      // place, so what disappears from a section is what the database says.
      await reload();
      if (item.recur_rule !== null && row.state === 'open') {
        // A recurring Done ADVANCED the row rather than closing it (0030), so
        // the way back is the occurrence undo — armed with the `updated_at`
        // the advance returned, which is what proves nothing happened since.
        setUndoable({
          kind: 'occurrence',
          id: item.id,
          title: item.title,
          updatedAt: row.updated_at,
          what: 'done',
        });
      } else {
        // A one-time Done — or the FINAL occurrence of a series, which ends
        // the row in `done` without advancing. Both come back through
        // `reopen`, and reopening an ended series revives the series.
        setUndoable({ kind: 'state', id: item.id, title: item.title, from: 'done' });
      }
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      // **A STALE REFUSAL MEANS THIS SCREEN IS OUT OF DATE**, so it re-reads
      // rather than leaving him looking at a list the database disagrees with.
      if (isStaleMyDayWrite(caught)) await reload();
    }
  }

  /**
   * **ONE RECOVERY PATH FOR BOTH WAYS OFF THE LIST.** Whether he ticked it off
   * or decided he no longer needed it, the way back is the same real server
   * `reopen` — told which state it is undoing, so it cannot resurrect
   * something that has since moved on.
   */
  async function undo(): Promise<void> {
    const client = getSupabaseClient();
    const target = undoable;
    if (!client || target === null) return;
    setNotice(null);
    try {
      if (target.kind === 'occurrence') {
        // **RESTORED FROM THE EVENT'S OWN SNAPSHOT, APPEND-ONLY** (0030): the
        // occurrence comes back exactly as it was, and the History keeps both
        // the advance and the change of mind.
        await undoMyDayOccurrence(client, target.id, target.updatedAt);
      } else {
        await reopenMyDayItem(client, target.id, target.from);
      }
      await reload();
      setUndoable(null);
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      // A stale refusal — or the occurrence undo's own "the follow-up has
      // moved on" (CL047) — means this bar is offering something the server
      // can no longer honestly do, so it goes rather than inviting retries.
      if (isStaleMyDayWrite(caught) || isUndoExpired(caught)) {
        setUndoable(null);
        await reload();
      }
    }
  }

  /**
   * Dismiss — *"I no longer need this"*, which is **not** *"I did this"*.
   *
   * A real server writer, not a local hide, and it offers the same Undo:
   * deciding you do not need something is exactly the kind of decision people
   * change their mind about thirty seconds later.
   */
  async function dismiss(item: MyDayListRow): Promise<void> {
    const client = getSupabaseClient();
    if (!client) return;
    setNotice(null);
    setMenuFor(null);
    try {
      await dismissMyDayItem(client, item.id, 'open');
      await reload();
      setUndoable({ kind: 'state', id: item.id, title: item.title, from: 'dismissed' });
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      if (isStaleMyDayWrite(caught)) await reload();
    }
  }

  /**
   * *"Skip this one"* — the series continues (0030, D-049). If this was the
   * FINAL occurrence the row ends `dismissed` instead of advancing, and the
   * way back is `reopen` rather than the occurrence undo.
   */
  async function skip(item: MyDayListRow): Promise<void> {
    const client = getSupabaseClient();
    if (!client) return;
    setNotice(null);
    setMenuFor(null);
    try {
      const row = await skipMyDayOccurrence(client, item.id, item.updated_at);
      await reload();
      if (row.state === 'open') {
        setUndoable({
          kind: 'occurrence',
          id: item.id,
          title: item.title,
          updatedAt: row.updated_at,
          what: 'skipped',
        });
      } else {
        setUndoable({ kind: 'state', id: item.id, title: item.title, from: 'dismissed' });
      }
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      if (isStaleMyDayWrite(caught)) await reload();
    }
  }

  /**
   * *"End the series"* — the other explicit meaning. The row keeps its repeat
   * metadata and Reopen revives the series, so the same Undo bar works.
   */
  async function endSeries(item: MyDayListRow): Promise<void> {
    const client = getSupabaseClient();
    if (!client) return;
    setNotice(null);
    setMenuFor(null);
    try {
      await endMyDaySeries(client, item.id, 'open');
      await reload();
      setUndoable({ kind: 'state', id: item.id, title: item.title, from: 'dismissed' });
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      if (isStaleMyDayWrite(caught)) await reload();
    }
  }

  /**
   * The quick High ⇄ Normal flip. It travels through the SAME edit writer the
   * Edit sheet uses — re-sending what the row already says with only the
   * priority changed — so the version token still guards it and the event log
   * records a priority diff and nothing else.
   */
  async function togglePriority(item: MyDayListRow): Promise<void> {
    const client = getSupabaseClient();
    if (!client) return;
    setNotice(null);
    setMenuFor(null);
    try {
      await updateMyDayItem(client, item.id, item.updated_at, {
        title: item.title,
        description: item.description,
        projectId: item.project_id,
        priority: item.priority === 'high' ? 'normal' : 'high',
      });
      await reload();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      if (isStaleMyDayWrite(caught)) await reload();
    }
  }

  /** Wake a snoozed follow-up now. Its true bucket returns immediately. */
  async function wake(item: MyDayListRow): Promise<void> {
    const client = getSupabaseClient();
    if (!client) return;
    setNotice(null);
    setMenuFor(null);
    try {
      await unsnoozeMyDayItem(client, item.id, item.updated_at);
      await reload();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      if (isStaleMyDayWrite(caught)) await reload();
    }
  }

  /** Detach the Capture. **The Project stays** (D-051) — evidence, not identity. */
  async function unlinkCapture(item: MyDayListRow): Promise<void> {
    const client = getSupabaseClient();
    if (!client) return;
    setNotice(null);
    setMenuFor(null);
    try {
      await unlinkMyDayCapture(client, item.id, item.updated_at);
      await reload();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      if (isStaleMyDayWrite(caught)) await reload();
    }
  }

  return (
    <main className={`cl-screen my-day-screen${undoable ? ' my-day-screen--undo' : ''}`}>
      <p className="my-day-back project-home-back">
        <Link className="cl-link" to="/">
          ‹ Projects
        </Link>
      </p>

      <h1 className="cl-screen-title">My Day</h1>
      {timezone ? (
        <p className="my-day-lead">
          Today is worked out in your Crewlink time zone, <strong>{timezone}</strong>.
        </p>
      ) : null}

      {/* WD-3: one honest line, only when it is true and only when it matters
          — a timed follow-up exists and the phone has notifications off. The
          follow-ups themselves are unaffected. */}
      {notificationsDenied && items.some((item) => item.due_time !== null) ? (
        <p className="my-day-lead my-day-notifications-off">
          Phone notifications are off, so Crewlink can’t give you a heads-up before timed
          follow-ups. You can turn them on in your phone’s Settings.
        </p>
      ) : null}

      {/* **THE FORM IS A DELIBERATE ACT, NOT THE VIEW.** It used to sit open at
          the top of the screen and pushed the actual follow-ups off a phone —
          the thing he opens My Day to read. It is now the Projects sheet
          pattern, opened from here. */}
      <p className="my-day-actions">
        <button type="button" className="cl-button" onClick={() => setAdding(true)}>
          + Add follow-up
        </button>
        {/* The append-only record of what happened (0030) — a separate screen,
            because a list he scans and a trail he consults are different
            reading postures. */}
        <Link className="cl-link my-day-history-link" to={MY_DAY_HISTORY_PATH}>
          History
        </Link>
      </p>

      {notice ? (
        <p className="cl-error" role="alert">
          {notice}
        </p>
      ) : null}

      {status === 'loading' ? <p className="my-day-lead">Loading…</p> : null}

      {status === 'failed' ? (
        <p className="cl-error" role="alert">
          {error ?? 'Crewlink could not load your follow-ups.'}
        </p>
      ) : null}

      {status === 'ready' ? (
        <>
          <Section
            title="Overdue"
            bucket="overdue"
            items={buckets.overdue}
            onDone={onDone}
            onMore={setMenuFor}
          />
          <Section
            title="Today"
            bucket="today"
            items={buckets.today}
            onDone={onDone}
            onMore={setMenuFor}
          />
          <Section
            title="Upcoming"
            bucket="upcoming"
            items={buckets.upcoming}
            onDone={onDone}
            onMore={setMenuFor}
          />
          {/* **RESTING, NOT GONE.** A snoozed follow-up stays visible in its
              own section — hidden entirely it would be indistinguishable from
              one that was lost, which is the S12-01 lesson all over again. The
              section only renders when something is actually resting. */}
          {buckets.snoozed.length > 0 ? (
            <Section
              title="Snoozed"
              bucket="snoozed"
              items={buckets.snoozed}
              onDone={onDone}
              onMore={setMenuFor}
            />
          ) : null}
        </>
      ) : null}

      {/* **UNDO HAS TO BE WHERE HE IS LOOKING, AND IT MUST NOT SCROLL AWAY.**
          It used to render near the top of the screen. Tick something off far
          down a list on a phone and the item vanishes while its only recovery
          sits off-screen above — which reads exactly like a follow-up that was
          lost. Pinned to the bottom, it is next to the thumb that just tapped
          Done, and it stays until he acts. **It is still a real `reopen`, never
          a local un-hide.** */}
      {undoable ? (
        <p className="my-day-undo" role="status">
          {/* It says WHICH thing happened. "Done" and "Dismissed" are different
              facts, and a bar that said the same for both would leave the Undo
              beside it ambiguous. */}
          <span>
            {describeUndoable(undoable)} — {undoable.title}
          </span>
          <span className="my-day-undo-actions">
            <button
              type="button"
              className="cl-button cl-button--quiet"
              onClick={() => void undo()}
            >
              Undo
            </button>
            {/* **"HIDE", NOT "DISMISS".** Dismiss now means something specific
                about a follow-up, and one word must not mean two things on one
                screen. This closes the bar and writes nothing. */}
            <button
              type="button"
              className="cl-button cl-button--quiet"
              aria-label="Hide"
              onClick={() => setUndoable(null)}
            >
              ✕
            </button>
          </span>
        </p>
      ) : null}

      {menuFor ? (
        <ItemActionsSheet
          item={menuFor}
          onCancel={() => setMenuFor(null)}
          onEdit={() => {
            setEditing(menuFor);
            setMenuFor(null);
          }}
          onReschedule={() => {
            setRescheduling(menuFor);
            setMenuFor(null);
          }}
          onTogglePriority={() => void togglePriority(menuFor)}
          onSnooze={() => {
            setSnoozing(menuFor);
            setMenuFor(null);
          }}
          onWake={() => void wake(menuFor)}
          onLinkCapture={() => {
            setLinking(menuFor);
            setMenuFor(null);
          }}
          onUnlinkCapture={() => void unlinkCapture(menuFor)}
          onDismiss={() => void dismiss(menuFor)}
          onSkip={() => void skip(menuFor)}
          onEndSeries={() => void endSeries(menuFor)}
        />
      ) : null}

      {editing ? (
        <EditFollowUpSheet
          item={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setUndoable(null);
            void reload();
          }}
        />
      ) : null}

      {rescheduling ? (
        <RescheduleSheet
          item={rescheduling}
          crewlinkTimeZone={timezone}
          onCancel={() => setRescheduling(null)}
          onSaved={() => {
            setRescheduling(null);
            setUndoable(null);
            void reload();
          }}
        />
      ) : null}

      {snoozing ? (
        <SnoozeSheet
          item={snoozing}
          crewlinkTimeZone={timezone}
          onCancel={() => setSnoozing(null)}
          onSaved={() => {
            setSnoozing(null);
            setUndoable(null);
            void reload();
          }}
        />
      ) : null}

      {linking ? (
        <CapturePickerSheet
          item={linking}
          onCancel={() => setLinking(null)}
          onSaved={() => {
            setLinking(null);
            setUndoable(null);
            void reload();
          }}
        />
      ) : null}

      {adding ? (
        <AddFollowUpSheet
          crewlinkTimeZone={timezone}
          onCancel={() => setAdding(false)}
          onCreated={() => {
            // **THE SERVER SPEAKS FIRST**, here too: the sheet closes and the
            // list is re-read rather than optimistically patched.
            setAdding(false);
            setUndoable(null);
            void reload();
          }}
        />
      ) : null}
    </main>
  );
}

/** What the bar says happened. Done, Dismissed and Skipped are different facts. */
function describeUndoable(
  target:
    | { kind: 'state'; from: 'done' | 'dismissed' }
    | { kind: 'occurrence'; what: 'done' | 'skipped' },
): string {
  if (target.kind === 'occurrence') return target.what === 'done' ? 'Done' : 'Skipped';
  return target.from === 'done' ? 'Done' : 'Dismissed';
}

/**
 * CL047 — the occurrence undo's own *"the follow-up has moved on"*. Read the
 * same way every My Day code is read: **from the code the writer raised, never
 * by parsing prose.**
 */
function isUndoExpired(error: unknown): boolean {
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const raw = error as { code?: unknown; message?: unknown } | null;
  return `${text(raw?.code)} ${text(raw?.message)}`.includes('CL047');
}

/** The CLOSED repeat vocabulary (0030, D-049), each said as Brandon says it. */
const RECUR_LABEL: Readonly<Record<MyDayRecurRule, string>> = {
  daily: 'Daily',
  weekdays: 'Weekdays',
  weekly: 'Weekly',
  every_2_weeks: 'Every 2 weeks',
  monthly: 'Monthly',
};

/**
 * One section. **An empty one says so in a sentence** rather than showing an
 * empty frame — a blank box reads as broken, and "nothing overdue" is good news
 * worth stating.
 */
function Section({
  title,
  bucket,
  items,
  onDone,
  onMore,
}: {
  readonly title: string;
  readonly bucket: MyDayBucket;
  readonly items: readonly MyDayListRow[];
  readonly onDone: (item: MyDayListRow) => void;
  readonly onMore: (item: MyDayListRow) => void;
}) {
  return (
    <section className="my-day-section" aria-labelledby={`my-day-${bucket}`}>
      <h2 id={`my-day-${bucket}`}>{title}</h2>
      {items.length === 0 ? (
        <p className="my-day-empty">Nothing {title.toLowerCase()}.</p>
      ) : (
        <ul className="my-day-list">
          {items.map((item) => (
            <li key={item.id} className="my-day-item">
              <div className="my-day-item-main">
                <span className="my-day-item-title">
                  {item.title}
                  {/* **THE FLAGS ARE THE ROW'S OWN COLUMNS, SHOWN AS WORDS.**
                      High sorts first within its section on the server; the
                      chip only makes the server's ordering legible. */}
                  {item.priority === 'high' ? (
                    <span className="my-day-flag my-day-flag--high">High</span>
                  ) : null}
                  {item.recur_rule !== null ? (
                    <span className="my-day-flag">↻ {RECUR_LABEL[item.recur_rule]}</span>
                  ) : null}
                </span>
                <span className="my-day-item-when">
                  <When item={item} />
                </span>
                {/* A resting row says until WHEN, rendered in the same
                    canonical zone its bucket was decided in. The authored
                    schedule above is untouched — a snooze never rewrites it. */}
                {item.bucket === 'snoozed' && item.snoozed_until !== null ? (
                  <span className="my-day-item-when">
                    Snoozed until{' '}
                    {formatInstant(item.snoozed_until, item.crewlink_timezone, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    }) || item.snoozed_until}
                  </span>
                ) : null}
                {item.description ? (
                  <span className="my-day-item-description">{item.description}</span>
                ) : null}
                <ItemLink item={item} />
              </div>
              {/* **TWO CONTROLS, NOT FIVE.** Done is the thing he does most,
                  so it stays on the row; everything else lives behind one
                  compact opener. A row of Edit / Reschedule / Dismiss buttons
                  would turn a list he scans in three seconds into a wall. */}
              <span className="my-day-item-actions">
                <button
                  type="button"
                  className="cl-button cl-button--quiet my-day-done"
                  onClick={() => onDone(item)}
                  aria-label={`Done — ${item.title}`}
                >
                  Done
                </button>
                <button
                  type="button"
                  className="cl-button cl-button--quiet my-day-more"
                  onClick={() => onMore(item)}
                  aria-label={`More — ${item.title}`}
                >
                  ⋯
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * When it is due, said honestly.
 *
 * A day-only item shows **its date and nothing else** — no invented o'clock. A
 * timed one shows the instant rendered in the canonical zone, which is the same
 * zone the bucket was decided in.
 */
function When({ item }: { readonly item: MyDayListRow }) {
  if (item.schedule_kind === 'day_only' || item.due_at === null) {
    return <>{item.due_date}</>;
  }
  const rendered = formatInstant(item.due_at, item.crewlink_timezone, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return <>{rendered === '' ? item.due_date : rendered}</>;
}

/**
 * The record this follow-up came from, if it still exists.
 *
 * **A CAPTURE OPENS AS A CAPTURE, THROUGH ITS OWN PROJECT** — the shared route
 * helper, so it can never be built from whatever Project happens to be on
 * screen. **A link that has gone away simply is not rendered**: the follow-up
 * survives its record (0028), and offering a dead link would be worse than
 * offering none.
 */
function ItemLink({ item }: { readonly item: MyDayListRow }) {
  if (item.capture_id !== null && item.project_id !== null) {
    const label =
      item.capture_number === null
        ? 'Open the Capture'
        : `Open CAP-${String(item.capture_number).padStart(4, '0')}`;
    return (
      <Link className="my-day-item-link" to={captureDetailPath(item.project_id, item.capture_id)}>
        {label}
      </Link>
    );
  }
  if (item.project_id !== null) {
    return (
      <Link className="my-day-item-link" to={`/projects/${item.project_id}`}>
        {item.project_name === null ? 'Open the Project' : `Open ${item.project_name}`}
      </Link>
    );
  }
  return null;
}

/**
 * Add a follow-up.
 *
 * **TIME IS GENUINELY OPTIONAL, AND THE FORM SAYS SO IN WORDS.** The field is
 * labelled optional and the help text states what leaving it empty means, so
 * the rule is discoverable rather than a surprise.
 *
 * The authored timezone travels **only with a time**, and it is the **canonical
 * Crewlink zone** — the one in Settings, not the one the handset reports. That
 * is what makes *"9am"* mean 9am in his working day whether he is at the site or
 * on a plane. The server keeps it beside the instant so the record still knows
 * what he meant later.
 *
 * **WITHOUT A CANONICAL ZONE, A TIME CANNOT BE AUTHORED HONESTLY**, so the time
 * field is unavailable and says why. A day-only follow-up still works, because
 * it needs no zone at all.
 *
 * **IT IS THE SHEET THE APP ALREADY HAS** — the same scrim, handle, title,
 * full-width primary button and Cancel as New Project (`CreateProjectSheet`,
 * design export S04). Nothing new was invented: a second sheet vocabulary would
 * be a second thing to keep consistent, and this one is already the shape
 * Brandon has learned. **Cancel writes nothing**, and a refusal keeps the sheet
 * and everything he typed.
 */
function AddFollowUpSheet({
  crewlinkTimeZone,
  onCancel,
  onCreated,
}: {
  readonly crewlinkTimeZone: string | null;
  readonly onCancel: () => void;
  readonly onCreated: () => void;
}) {
  const { projects } = useProjects();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [projectId, setProjectId] = useState('');
  const [priority, setPriority] = useState<MyDayPriority>('normal');
  /** '' is one-time. The values are the CLOSED vocabulary and nothing else. */
  const [recurRule, setRecurRule] = useState<'' | MyDayRecurRule>('');
  const [recurEndsOn, setRecurEndsOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const canSave = title.trim() !== '' && dueDate !== '' && !busy;
  /** A time needs a zone to mean anything; a whole-day follow-up does not. */
  const canSetTime = crewlinkTimeZone !== null;

  async function save(): Promise<void> {
    const client = getSupabaseClient();
    if (!client || !canSave) return;
    setBusy(true);
    setNotice(null);
    try {
      await createMyDayItem(client, {
        title: title.trim(),
        description: description.trim() === '' ? null : description.trim(),
        dueDate,
        // **EMPTY MEANS DAY-ONLY.** Nothing substitutes a time here.
        dueTime: dueTime === '' ? null : dueTime,
        // **THE CANONICAL ZONE, NEVER THE DEVICE'S.**
        timezone: dueTime === '' ? null : crewlinkTimeZone,
        projectId: projectId === '' ? null : projectId,
        // **NO CAPTURE PICKER AT AUTHORING TIME.** An item can carry one —
        // linking is its own act in the row's menu (WD-1), where the item
        // already exists to link to.
        captureId: null,
        priority,
        recurRule: recurRule === '' ? null : recurRule,
        // An end without a repeat is refused by the server; the field is only
        // offered under a rule, so nothing here has to police it.
        recurEndsOn: recurRule === '' || recurEndsOn === '' ? null : recurEndsOn,
      });
      // **WD-3: THE CONTEXTUAL PERMISSION MOMENT.** He just made a TIMED
      // follow-up matter, so this — and only a moment like this — may ask the
      // OS for notification permission. Day-only creates ask nothing (they
      // never notify), lifecycle runs never prompt, and a denied state is
      // respected: the service prompts only while the OS still says 'prompt'.
      if (dueTime !== '') {
        void myDayNotificationService().run({ mayPrompt: true });
      }
      setTitle('');
      setDescription('');
      setDueDate('');
      setDueTime('');
      setProjectId('');
      setPriority('normal');
      setRecurRule('');
      setRecurEndsOn('');
      onCreated();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="sheet-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Add follow-up"
      onClick={(event) => {
        // Tapping the dimmed area cancels — but never mid-save, which would
        // leave him unsure whether the follow-up exists.
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <section className="sheet my-day-sheet" aria-labelledby="my-day-add-heading">
        <div className="sheet-handle" aria-hidden="true" />
        <h2 id="my-day-add-heading" className="sheet-title">
          Add follow-up
        </h2>

        <label className="cl-field">
          <span className="cl-label">What needs doing</span>
          <input
            className="cl-input"
            type="text"
            value={title}
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <label className="cl-field">
          <span className="cl-label">
            Notes <span className="my-day-optional">— optional</span>
          </span>
          <textarea
            className="cl-input"
            rows={2}
            value={description}
            maxLength={2000}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <label className="cl-field">
          <span className="cl-label">Day</span>
          <input
            className="cl-input"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        </label>

        <label className="cl-field">
          <span className="cl-label">
            Time <span className="my-day-optional">— optional</span>
          </span>
          <input
            className="cl-input"
            type="time"
            value={dueTime}
            disabled={!canSetTime}
            onChange={(event) => setDueTime(event.target.value)}
          />
        </label>
        <p className="my-day-hint">
          {canSetTime
            ? `Leave the time empty for a whole-day follow-up. A time is saved in your Crewlink time zone, ${crewlinkTimeZone}.`
            : 'Set your time zone in Settings before adding a follow-up with a time. A whole-day follow-up works either way.'}
        </p>

        <label className="cl-field">
          <span className="cl-label">
            Project <span className="my-day-optional">— optional</span>
          </span>
          <select
            className="cl-input"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
          >
            <option value="">No Project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>

        <label className="cl-field">
          <span className="cl-label">Priority</span>
          <select
            className="cl-input"
            value={priority}
            onChange={(event) => setPriority(event.target.value === 'high' ? 'high' : 'normal')}
          >
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </label>

        <label className="cl-field">
          <span className="cl-label">
            Repeat <span className="my-day-optional">— optional</span>
          </span>
          <RecurRuleSelect value={recurRule} onChange={setRecurRule} />
        </label>
        {recurRule !== '' ? (
          <>
            <label className="cl-field">
              <span className="cl-label">
                Repeat until <span className="my-day-optional">— optional</span>
              </span>
              <input
                className="cl-input"
                type="date"
                value={recurEndsOn}
                onChange={(event) => setRecurEndsOn(event.target.value)}
              />
            </label>
            <p className="my-day-hint">
              One at a time: the next one appears when this one is done or skipped.
            </p>
          </>
        ) : null}

        {/* **A REFUSAL KEEPS THE SHEET AND KEEPS HIS TYPING.** Closing on failure
          would throw away what he wrote and leave him guessing. */}
        {notice ? (
          <p className="cl-error" role="alert">
            {notice}
          </p>
        ) : null}

        <button
          type="button"
          className="cl-button sheet-submit"
          disabled={!canSave}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Add follow-up'}
        </button>

        {/* **CANCEL WRITES NOTHING** — it closes, and the database never hears
          about it. Disabled mid-save for the same reason the scrim is. */}
        <button className="sheet-cancel" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </section>
    </div>
  );
}

/**
 * The compact action menu for one follow-up — S12-02, grown by WD-1.
 *
 * **ONE OPENER, DELIBERATE ACTIONS.** Edit changes what it says. Reschedule
 * changes when it is due. Snooze rests it without moving either. They are
 * separate here because they are separate on the server: **fixing a typo must
 * not be able to move a reminder**, and a menu that blurred them would invite
 * exactly that.
 *
 * **A RECURRING FOLLOW-UP HAS NO PLAIN DISMISS** — the server refuses one
 * (CL043) because *"I no longer need this"* is ambiguous about a series: this
 * one, or all of them? The menu asks the same question in words, offering
 * **Skip this one** and **End the series** instead, so the ambiguous button
 * simply does not exist here.
 */
function ItemActionsSheet({
  item,
  onCancel,
  onEdit,
  onReschedule,
  onTogglePriority,
  onSnooze,
  onWake,
  onLinkCapture,
  onUnlinkCapture,
  onDismiss,
  onSkip,
  onEndSeries,
}: {
  readonly item: MyDayListRow;
  readonly onCancel: () => void;
  readonly onEdit: () => void;
  readonly onReschedule: () => void;
  readonly onTogglePriority: () => void;
  readonly onSnooze: () => void;
  readonly onWake: () => void;
  readonly onLinkCapture: () => void;
  readonly onUnlinkCapture: () => void;
  readonly onDismiss: () => void;
  readonly onSkip: () => void;
  readonly onEndSeries: () => void;
}) {
  const recurring = item.recur_rule !== null;
  return (
    <div
      className="sheet-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={`Actions — ${item.title}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section className="sheet my-day-sheet">
        <div className="sheet-handle" aria-hidden="true" />
        <h2 className="sheet-title">{item.title}</h2>

        <button type="button" className="cl-button sheet-submit" onClick={onEdit}>
          Edit
        </button>
        <button type="button" className="cl-button sheet-submit" onClick={onReschedule}>
          Reschedule
        </button>
        {/* The flip reads the row it is flipping, so the label says what will
            HAPPEN, not what is. */}
        <button type="button" className="cl-button sheet-submit" onClick={onTogglePriority}>
          {item.priority === 'high' ? 'Back to normal priority' : 'Mark high priority'}
        </button>
        {/* A resting item offers the way back; either way it can be re-rested. */}
        {item.bucket === 'snoozed' ? (
          <button type="button" className="cl-button sheet-submit" onClick={onWake}>
            Wake now
          </button>
        ) : null}
        <button type="button" className="cl-button sheet-submit" onClick={onSnooze}>
          Snooze…
        </button>
        {item.capture_id === null ? (
          <button type="button" className="cl-button sheet-submit" onClick={onLinkCapture}>
            Link a Capture…
          </button>
        ) : (
          <button type="button" className="cl-button sheet-submit" onClick={onUnlinkCapture}>
            Unlink the Capture
          </button>
        )}
        {recurring ? (
          <>
            {/* **THE TWO HONEST MEANINGS, EACH IN ITS OWN WORDS** (D-049). */}
            <button type="button" className="cl-button sheet-submit" onClick={onSkip}>
              Skip this one
            </button>
            <button type="button" className="cl-button sheet-submit" onClick={onEndSeries}>
              End the series
            </button>
          </>
        ) : (
          /* **NOT DONE, AND IT SAYS SO IN WORDS.** "I no longer need this" and
             "I did this" are different facts about his day, and the record
             keeps them apart. */
          <button type="button" className="cl-button sheet-submit" onClick={onDismiss}>
            Dismiss — no longer needed
          </button>
        )}

        <button className="sheet-cancel" type="button" onClick={onCancel}>
          Cancel
        </button>
      </section>
    </div>
  );
}

/**
 * Edit — **what it says, never when it is due** (S12-02).
 *
 * There is no Day or Time field here, and no schedule parameter on the writer
 * either: the split is structural, not a matter of this form being careful.
 * Rescheduling is its own sheet.
 *
 * The `updated_at` the row was showing travels with the save, so **an edit
 * decided against a stale list is refused rather than quietly overwriting a
 * newer title** — and the refusal keeps the sheet and everything he typed.
 */
function EditFollowUpSheet({
  item,
  onCancel,
  onSaved,
}: {
  readonly item: MyDayListRow;
  readonly onCancel: () => void;
  readonly onSaved: () => void;
}) {
  const { projects } = useProjects();
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description ?? '');
  const [projectId, setProjectId] = useState(item.project_id ?? '');
  const [priority, setPriority] = useState<MyDayPriority>(item.priority);
  const [recurRule, setRecurRule] = useState<'' | MyDayRecurRule>(item.recur_rule ?? '');
  const [recurEndsOn, setRecurEndsOn] = useState(item.recur_ends_on ?? '');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const canSave = title.trim() !== '' && !busy;

  async function save(): Promise<void> {
    const client = getSupabaseClient();
    if (!client || !canSave) return;
    setBusy(true);
    setNotice(null);
    try {
      const row = await updateMyDayItem(client, item.id, item.updated_at, {
        title: title.trim(),
        description: description.trim() === '' ? null : description.trim(),
        projectId: projectId === '' ? null : projectId,
        priority,
      });
      // **THE REPEAT IS ITS OWN WRITER, CHAINED ON THE RETURNED TOKEN.** The
      // edit writer structurally cannot touch the schedule, and a repeat's
      // anchor IS schedule — so the change travels through
      // `set_my_day_recurrence`, versioned against the `updated_at` the edit
      // just returned. Called only when the repeat actually changed: an edit
      // silent about the repeat says nothing about it.
      const wantRule = recurRule === '' ? null : recurRule;
      const wantEnds = recurRule === '' || recurEndsOn === '' ? null : recurEndsOn;
      if (wantRule !== item.recur_rule || wantEnds !== item.recur_ends_on) {
        await setMyDayRecurrence(client, item.id, row.updated_at, {
          rule: wantRule,
          endsOn: wantEnds,
        });
      }
      onSaved();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="sheet-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Edit follow-up"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <section className="sheet my-day-sheet">
        <div className="sheet-handle" aria-hidden="true" />
        <h2 className="sheet-title">Edit follow-up</h2>

        <label className="cl-field">
          <span className="cl-label">What needs doing</span>
          <input
            className="cl-input"
            type="text"
            value={title}
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <label className="cl-field">
          <span className="cl-label">
            Notes <span className="my-day-optional">— optional</span>
          </span>
          <textarea
            className="cl-input"
            rows={2}
            value={description}
            maxLength={2000}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <label className="cl-field">
          <span className="cl-label">
            Project <span className="my-day-optional">— optional</span>
          </span>
          <select
            className="cl-input"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
          >
            <option value="">No Project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>

        <label className="cl-field">
          <span className="cl-label">Priority</span>
          <select
            className="cl-input"
            value={priority}
            onChange={(event) => setPriority(event.target.value === 'high' ? 'high' : 'normal')}
          >
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </label>

        <label className="cl-field">
          <span className="cl-label">
            Repeat <span className="my-day-optional">— optional</span>
          </span>
          <RecurRuleSelect value={recurRule} onChange={setRecurRule} />
        </label>
        {recurRule !== '' ? (
          <label className="cl-field">
            <span className="cl-label">
              Repeat until <span className="my-day-optional">— optional</span>
            </span>
            <input
              className="cl-input"
              type="date"
              value={recurEndsOn}
              onChange={(event) => setRecurEndsOn(event.target.value)}
            />
          </label>
        ) : null}
        {item.recur_rule !== null && recurRule === '' ? (
          <p className="my-day-hint">
            Turning the repeat off keeps this follow-up on its current day — it stops repeating, it
            does not go away.
          </p>
        ) : null}

        {/* **THE SCHEDULE IS SHOWN, NOT EDITABLE HERE** — so he can see he is
            not about to change it, and knows where to go if he wants to. */}
        <p className="my-day-hint">
          Due <When item={item} />. Use Reschedule to change when it is due.
        </p>

        {notice ? (
          <p className="cl-error" role="alert">
            {notice}
          </p>
        ) : null}

        <button
          type="button"
          className="cl-button sheet-submit"
          disabled={!canSave}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </button>

        <button className="sheet-cancel" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </section>
    </div>
  );
}

/**
 * Reschedule — **the only place a schedule is re-authored** (S12-02).
 *
 * **LEAVING THE TIME EMPTY STILL MEANS DAY-ONLY**, in both directions: he can
 * add a time to a whole-day follow-up, or drop the time off one and give
 * himself the day back. Nothing invents an hour on the way through.
 *
 * **NO TIMEZONE TRAVELS FROM HERE.** The server reads the canonical saved zone
 * itself, which is why a phone in another country cannot re-author *"9am"* into
 * somebody else's morning. The zone is named on screen so he can see which one
 * he is committing to.
 */
function RescheduleSheet({
  item,
  crewlinkTimeZone,
  onCancel,
  onSaved,
}: {
  readonly item: MyDayListRow;
  readonly crewlinkTimeZone: string | null;
  readonly onCancel: () => void;
  readonly onSaved: () => void;
}) {
  const [dueDate, setDueDate] = useState(item.due_date);
  const [dueTime, setDueTime] = useState(item.due_time === null ? '' : item.due_time.slice(0, 5));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const canSetTime = crewlinkTimeZone !== null;
  const canSave = dueDate !== '' && !busy;

  async function save(): Promise<void> {
    const client = getSupabaseClient();
    if (!client || !canSave) return;
    setBusy(true);
    setNotice(null);
    try {
      await rescheduleMyDayItem(client, item.id, item.updated_at, {
        dueDate,
        // **EMPTY MEANS DAY-ONLY**, and the server clears the zone and the
        // instant with it rather than leaving a half-timed row behind.
        dueTime: dueTime === '' ? null : dueTime,
      });
      // **WD-3: rescheduling onto a TIME is the other contextual permission
      // moment** — same rule as a timed create, same no-nag posture.
      if (dueTime !== '') {
        void myDayNotificationService().run({ mayPrompt: true });
      }
      onSaved();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="sheet-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Reschedule follow-up"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <section className="sheet my-day-sheet">
        <div className="sheet-handle" aria-hidden="true" />
        <h2 className="sheet-title">Reschedule</h2>
        <p className="my-day-hint">{item.title}</p>

        <label className="cl-field">
          <span className="cl-label">Day</span>
          <input
            className="cl-input"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        </label>

        <label className="cl-field">
          <span className="cl-label">
            Time <span className="my-day-optional">— optional</span>
          </span>
          <input
            className="cl-input"
            type="time"
            value={dueTime}
            disabled={!canSetTime}
            onChange={(event) => setDueTime(event.target.value)}
          />
        </label>
        <p className="my-day-hint">
          {canSetTime
            ? `Clear the time to make it a whole-day follow-up. A time is saved in your Crewlink time zone, ${crewlinkTimeZone}.`
            : 'Set your time zone in Settings before giving this a time. A whole-day follow-up works either way.'}
        </p>

        {notice ? (
          <p className="cl-error" role="alert">
            {notice}
          </p>
        ) : null}

        <button
          type="button"
          className="cl-button sheet-submit"
          disabled={!canSave}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save new time'}
        </button>

        <button className="sheet-cancel" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </section>
    </div>
  );
}

/**
 * The one Repeat control, shared by Add and Edit so the vocabulary cannot
 * fork. **The options ARE the closed vocabulary** (0030, D-049) — the server
 * refuses anything else (CL045), and this select simply has nothing else to
 * offer.
 */
function RecurRuleSelect({
  value,
  onChange,
}: {
  readonly value: '' | MyDayRecurRule;
  readonly onChange: (value: '' | MyDayRecurRule) => void;
}) {
  return (
    <select
      className="cl-input"
      value={value}
      onChange={(event) => {
        const picked = event.target.value;
        onChange(
          picked === 'daily' ||
            picked === 'weekdays' ||
            picked === 'weekly' ||
            picked === 'every_2_weeks' ||
            picked === 'monthly'
            ? picked
            : '',
        );
      }}
    >
      <option value="">Does not repeat</option>
      <option value="daily">Daily</option>
      <option value="weekdays">Weekdays</option>
      <option value="weekly">Weekly</option>
      <option value="every_2_weeks">Every 2 weeks</option>
      <option value="monthly">Monthly</option>
    </select>
  );
}

/**
 * Snooze — *"not now, wake me later"* (WD-1, D-050).
 *
 * **THE SCHEDULE IS NOT ON THIS SHEET AND CANNOT MOVE FROM IT.** The writer's
 * SET clause holds `snoozed_until` alone; when the snooze lapses the untouched
 * schedule puts the follow-up exactly where it belongs — including Overdue,
 * honestly.
 *
 * The instant is authored **in the canonical Crewlink timezone, never the
 * device's** — the due-time rule (INV-T17), applied to the one other wall time
 * a person types into My Day. Without a canonical zone the sheet says so and
 * offers nothing, exactly as the time field does. **Whether it is far enough
 * in the future is the SERVER'S call** (CL044): no clock here decides, and a
 * refusal keeps the sheet and what he picked.
 */
function SnoozeSheet({
  item,
  crewlinkTimeZone,
  onCancel,
  onSaved,
}: {
  readonly item: MyDayListRow;
  readonly crewlinkTimeZone: string | null;
  readonly onCancel: () => void;
  readonly onSaved: () => void;
}) {
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const canPick = crewlinkTimeZone !== null;
  const canSave = canPick && until !== '' && !busy;

  async function save(): Promise<void> {
    const client = getSupabaseClient();
    if (!client || !canSave || crewlinkTimeZone === null) return;
    const instant = wallTimeToInstant(crewlinkTimeZone, until);
    if (instant === null) {
      setNotice('Crewlink could not read that date and time.');
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await snoozeMyDayItem(client, item.id, item.updated_at, instant);
      onSaved();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="sheet-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Snooze follow-up"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <section className="sheet my-day-sheet">
        <div className="sheet-handle" aria-hidden="true" />
        <h2 className="sheet-title">Snooze</h2>
        <p className="my-day-hint">{item.title}</p>

        <label className="cl-field">
          <span className="cl-label">Until</span>
          <input
            className="cl-input"
            type="datetime-local"
            value={until}
            disabled={!canPick}
            onChange={(event) => setUntil(event.target.value)}
          />
        </label>
        <p className="my-day-hint">
          {canPick
            ? `It rests until then, in your Crewlink time zone, ${crewlinkTimeZone}. Its due day does not change — if that day passes, it comes back overdue.`
            : 'Set your time zone in Settings before snoozing — Crewlink needs it to know when "until" is.'}
        </p>

        {notice ? (
          <p className="cl-error" role="alert">
            {notice}
          </p>
        ) : null}

        <button
          type="button"
          className="cl-button sheet-submit"
          disabled={!canSave}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Snooze'}
        </button>

        <button className="sheet-cancel" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </section>
    </div>
  );
}

/** One row the picker can offer — a Capture plus the words that identify it. */
interface CapturePick {
  readonly captureId: string;
  readonly captureNumber: number;
  readonly projectName: string | null;
  readonly caption: string | null;
}

/**
 * Choose a Capture to link — WD-1 (D-051).
 *
 * **RETRIEVAL IS `search_captures`, THE ONE BOUNDARY THE APP ALREADY HAS**
 * (migration 0015) — global scope, stated explicitly, because a follow-up may
 * point at any Capture Brandon owns, not only the current Project's. There is
 * no second search engine here: the sheet hydrates the ids the database chose
 * and shows them in the database's order.
 *
 * **PICKING ONE SENDS THE ID AND NOTHING ELSE.** The server reads the
 * Capture's own Project and re-points the follow-up (0028's rule): nothing
 * this sheet displays participates in deciding anything.
 */
function CapturePickerSheet({
  item,
  onCancel,
  onSaved,
}: {
  readonly item: MyDayListRow;
  readonly onCancel: () => void;
  readonly onSaved: () => void;
}) {
  const [query, setQuery] = useState('');
  const [picks, setPicks] = useState<readonly CapturePick[]>([]);
  const [totalMatches, setTotalMatches] = useState(0);
  /**
   * True from mount and from every keystroke until that read answers. It is
   * set in the HANDLER, not the effect, so the effect only ever writes state
   * back from the server's answer.
   */
  const [searching, setSearching] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /** Search-as-you-type: only the newest ticket may write to state. */
  const generation = useRef(0);

  useEffect(() => {
    const client = getSupabaseClient();
    if (!client) return;
    generation.current += 1;
    const ticket = generation.current;
    void (async () => {
      try {
        const { data: hits, error: searchError } = await client.rpc('search_captures', {
          // **EXPLICIT GLOBAL SCOPE** (migration 0015): a follow-up may point
          // anywhere Brandon can read, and stating the scope is what keeps a
          // dropped field from ever quietly meaning it.
          p_scope: 'global',
          p_query: query.trim() === '' ? null : query,
          // A picker offers a handful, not a results screen. The TRUE count
          // arrives on every row, so the sheet can say what it is not showing.
          p_limit: 8,
        });
        if (searchError) throw searchError;
        const rows = hits ?? [];
        const ids = rows.map((hit) => hit.capture_id);
        if (ids.length === 0) {
          if (ticket !== generation.current) return;
          setPicks([]);
          setTotalMatches(0);
          setSearching(false);
          return;
        }

        // Hydrate the words a person recognises a Capture by. Three batched
        // reads, all RLS-filtered; the picker shows only what he could open.
        const [captures, projects, organizations] = await Promise.all([
          client.from('capture').select('*').in('id', ids),
          client.from('project').select('*'),
          client.from('capture_organization').select('*').in('capture_id', ids),
        ]);
        if (captures.error) throw captures.error;
        if (projects.error) throw projects.error;
        if (organizations.error) throw organizations.error;

        const captureById = new Map<string, CaptureRow>(
          (captures.data ?? []).map((row) => [row.id, row]),
        );
        const projectById = new Map<string, ProjectRow>(
          (projects.data ?? []).map((row) => [row.id, row]),
        );
        const organizationByCapture = new Map<string, CaptureOrganizationRow>(
          (organizations.data ?? []).map((row) => [row.capture_id, row]),
        );

        // **THE DATABASE'S ORDER, KEPT** — ids map through in the sequence
        // `search_captures` returned them, exactly as the Captures screen does.
        const hydrated = ids.flatMap((id): CapturePick[] => {
          const capture = captureById.get(id);
          if (!capture) return [];
          return [
            {
              captureId: capture.id,
              captureNumber: capture.capture_number,
              projectName: projectById.get(capture.project_id)?.name ?? null,
              caption: organizationByCapture.get(id)?.caption ?? null,
            },
          ];
        });
        if (ticket !== generation.current) return;
        setPicks(hydrated);
        setTotalMatches(rows[0]?.total_match_count ?? hydrated.length);
        setSearching(false);
      } catch (caught) {
        if (ticket !== generation.current) return;
        setNotice(describeMyDayFailure(caught));
        setSearching(false);
      }
    })();
  }, [query]);

  async function link(captureId: string): Promise<void> {
    const client = getSupabaseClient();
    if (!client || busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await linkMyDayCapture(client, item.id, item.updated_at, captureId);
      onSaved();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      setBusy(false);
    }
  }

  return (
    <div
      className="sheet-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Link a Capture"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <section className="sheet my-day-sheet">
        <div className="sheet-handle" aria-hidden="true" />
        <h2 className="sheet-title">Link a Capture</h2>
        <p className="my-day-hint">
          {item.title} — the follow-up will point at the Capture&apos;s own Project.
        </p>

        <label className="cl-field">
          <span className="cl-label">Search your Captures</span>
          <input
            className="cl-input"
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSearching(true);
            }}
          />
        </label>

        {notice ? (
          <p className="cl-error" role="alert">
            {notice}
          </p>
        ) : null}

        {searching ? <p className="my-day-empty">Searching…</p> : null}
        {!searching && picks.length === 0 && notice === null ? (
          <p className="my-day-empty">No Captures matched.</p>
        ) : null}

        <ul className="my-day-list">
          {picks.map((pick) => (
            <li key={pick.captureId} className="my-day-item">
              <div className="my-day-item-main">
                <span className="my-day-item-title">
                  CAP-{String(pick.captureNumber).padStart(4, '0')}
                </span>
                {pick.caption ? (
                  <span className="my-day-item-description">{pick.caption}</span>
                ) : null}
                {pick.projectName ? (
                  <span className="my-day-item-when">{pick.projectName}</span>
                ) : null}
              </div>
              <span className="my-day-item-actions">
                <button
                  type="button"
                  className="cl-button cl-button--quiet"
                  disabled={busy}
                  onClick={() => void link(pick.captureId)}
                >
                  Link
                </button>
              </span>
            </li>
          ))}
        </ul>
        {/* What is NOT shown, said plainly — never a list that reads complete. */}
        {!searching && totalMatches > picks.length ? (
          <p className="my-day-hint">
            Showing {picks.length} of {totalMatches} — keep typing to narrow it down.
          </p>
        ) : null}

        <button className="sheet-cancel" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </section>
    </div>
  );
}
